/** The one cost model, shared by the app, the weekly snapshot and (ported) the planner's cost.py; keep them in parity. */
import { type RouteKind, resolveRouteKind } from "./routeKind.ts"

export interface RouteCostVehicleType {
  name: string
  monthly_lease_cost: number
  miles_per_kwh: number
  labour_hourly_rate: number
  range_miles: number
}

export interface RouteCostConfig {
  driver_wage_rate_per_hour: number
  on_site_charging_cost_per_kwh?: number
  default_cost_per_kwh: number
  oaklands_cost_per_kwh?: number
  internal_charging_range_pct?: number
}

export interface RouteCostBreakdown {
  vehicle_lease_cost: number
  charging_cost: number
  charging_internal_miles?: number
  charging_external_miles?: number
  driver_wage_cost: number
  total_cost: number
  cost_per_parcel?: number
  total_forecast_volume?: number
  total_haulage_delivery_revenue: number
  /** Total is the agreed direct-injection price, all reported as labour. */
  is_direct_injection_priced?: boolean
  /** Stand-by day: driver hours only, no lease or charging. */
  is_standby_priced?: boolean
}

export interface CalculateRouteCostParams {
  vehicleType?: RouteCostVehicleType | null
  distanceMiles?: number | null
  durationMinutes?: number | null
  driverWageRatePerHour?: number | null
  config?: RouteCostConfig | null
  totalForecastVolume?: number | null
  totalHaulageDeliveryRevenue?: number | null
  monthlyLeaseCostOverride?: number | null
  /** Non-null replaces the whole model with this figure, reported as labour. */
  directInjectionCostGbp?: number | null
  /** Multiplies the labour line only; absent or below 1 reads as 1. */
  crewSize?: number | null
  /** A stand-by day is costed without a vehicle type. */
  routeKind?: RouteKind | null
}

export type StopWithHaulageRevenue = {
  slotType?: string | null
  stop_type?: string | null
  haulageDeliveryRevenueGbp?: number | null
  haulage_delivery_revenue_gbp?: number | null
}

export function sumHaulageDeliveryRevenueFromStops(
  stops: StopWithHaulageRevenue[] | null | undefined,
): number {
  if (!stops?.length) return 0
  return stops
    .filter((s) => s.slotType === "haulage_delivery" || s.stop_type === "haulage_delivery")
    .reduce(
      (sum, s) =>
        sum + (Number(s.haulageDeliveryRevenueGbp ?? s.haulage_delivery_revenue_gbp) || 0),
      0,
    )
}

/** Stop-like object that may be a direct injection carrying an agreed price. */
export type StopWithDirectInjectionCost = {
  slotType?: string | null
  stop_type?: string | null
  directInjectionCostGbp?: number | null
  direct_injection_cost_gbp?: number | null
}

/** The day wins, then the plan; never below 1 so a missing value cannot zero the wage line. */
export function resolveCrewSize(
  instanceCrewSize: number | null | undefined,
  planCrewSize: number | null | undefined
): number {
  const resolved = instanceCrewSize ?? planCrewSize ?? 1
  return resolved > 1 ? resolved : 1
}

/** Null when no stop is a direct injection; an unpriced injection counts as 0 so it shows up rather than being modelled. */
export function resolveDirectInjectionCost(
  stops: StopWithDirectInjectionCost[] | null | undefined,
): number | null {
  if (!stops?.length) return null
  const injections = stops.filter(
    (s) => s.slotType === "direct_injection" || s.stop_type === "direct_injection",
  )
  if (!injections.length) return null
  return injections.reduce(
    (sum, s) => sum + (Number(s.directInjectionCostGbp ?? s.direct_injection_cost_gbp) || 0),
    0,
  )
}

const DEFAULT_INTERNAL_CHARGING_RANGE_PCT = 70
const DEFAULT_OAKLANDS_COST_PER_KWH = 0.305

/** Labour rate: vehicle labour_hourly_rate, then param, then config. HGVs split mileage on-site/Oaklands; others split vehicle range on-site/off-site. */
export function calculateRouteCost(params: CalculateRouteCostParams): RouteCostBreakdown | null {
  const {
    vehicleType,
    distanceMiles,
    durationMinutes,
    driverWageRatePerHour,
    config,
    totalForecastVolume,
    totalHaulageDeliveryRevenue,
    monthlyLeaseCostOverride,
    directInjectionCostGbp,
    crewSize,
    routeKind,
  } = params

  // Ahead of the `!vehicleType` guard: stand-by routes have no vehicle type, and returning null would cost the spares at 0.
  if (routeKind === "standby") {
    const standbyRate =
      (vehicleType?.labour_hourly_rate != null && vehicleType.labour_hourly_rate > 0
        ? vehicleType.labour_hourly_rate
        : null) ??
      driverWageRatePerHour ??
      config?.driver_wage_rate_per_hour ??
      0
    const standbyCrew = crewSize != null && crewSize > 1 ? crewSize : 1
    const standbyCost =
      durationMinutes != null && durationMinutes > 0 && standbyRate > 0
        ? (durationMinutes / 60) * standbyRate * standbyCrew
        : 0
    return {
      vehicle_lease_cost: 0,
      charging_cost: 0,
      driver_wage_cost: standbyCost,
      total_cost: standbyCost,
      is_standby_priced: true,
      // No cost_per_parcel: nothing is collected on a stand-by day.
      total_forecast_volume: totalForecastVolume ?? undefined,
      total_haulage_delivery_revenue: totalHaulageDeliveryRevenue ?? 0,
    }
  }

  if (!vehicleType) {
    return null
  }

  // Priced, not costed: assumes a route with a direct injection is bought in whole (see migration 20260921120000).
  if (routeKind === "external" || directInjectionCostGbp != null) {
    // An unpriced external route is 0, not a model cost: visibly wrong gets fixed, plausibly wrong does not.
    const totalCost = directInjectionCostGbp ?? 0
    return {
      vehicle_lease_cost: 0,
      charging_cost: 0,
      driver_wage_cost: totalCost,
      total_cost: totalCost,
      is_direct_injection_priced: true,
      cost_per_parcel:
        totalForecastVolume != null && totalForecastVolume > 0
          ? totalCost / totalForecastVolume
          : undefined,
      total_forecast_volume: totalForecastVolume ?? undefined,
      total_haulage_delivery_revenue: totalHaulageDeliveryRevenue ?? 0,
    }
  }

  const monthlyCost =
    monthlyLeaseCostOverride != null && monthlyLeaseCostOverride >= 0
      ? monthlyLeaseCostOverride
      : vehicleType.monthly_lease_cost
  const vehicleLeaseCost = monthlyCost / 30

  const isHGV = vehicleType.name?.toUpperCase().startsWith("HGV") ?? false

  let chargingCost = 0
  let chargingInternalMiles: number | undefined
  let chargingExternalMiles: number | undefined
  if (distanceMiles != null && distanceMiles > 0) {
    const rangeMiles = vehicleType.range_miles ?? 0
    const milesPerKwh = vehicleType.miles_per_kwh ?? 0
    const onSiteRate = config?.on_site_charging_cost_per_kwh ?? 0
    const internalCostPerMile = milesPerKwh > 0 && onSiteRate >= 0 ? onSiteRate / milesPerKwh : 0

    if (isHGV) {
      const internalRangePct =
        config?.internal_charging_range_pct ?? DEFAULT_INTERNAL_CHARGING_RANGE_PCT
      const internalRangeFraction = Math.max(0, Math.min(100, internalRangePct)) / 100
      const oaklandsRate = config?.oaklands_cost_per_kwh ?? DEFAULT_OAKLANDS_COST_PER_KWH
      const oaklandsCostPerMile =
        milesPerKwh > 0 && oaklandsRate >= 0 ? oaklandsRate / milesPerKwh : 0

      const internalMiles = distanceMiles * internalRangeFraction
      const oaklandsMiles = distanceMiles - internalMiles

      chargingCost = internalMiles * internalCostPerMile + oaklandsMiles * oaklandsCostPerMile
      chargingInternalMiles = internalMiles
      chargingExternalMiles = oaklandsMiles
    } else {
      const internalRangePct =
        config?.internal_charging_range_pct ?? DEFAULT_INTERNAL_CHARGING_RANGE_PCT
      const internalRangeFraction = Math.max(0, Math.min(100, internalRangePct)) / 100
      const offSiteRate = config?.default_cost_per_kwh ?? 0
      const hasExternal = rangeMiles > 0 && offSiteRate >= 0 && milesPerKwh > 0
      const externalCostPerMile = hasExternal ? offSiteRate / milesPerKwh : 0

      const internalCapMiles = rangeMiles * internalRangeFraction
      const internalMiles = Math.min(distanceMiles, internalCapMiles)
      const externalMiles = hasExternal ? Math.max(0, distanceMiles - internalMiles) : 0

      chargingCost = internalMiles * internalCostPerMile + externalMiles * externalCostPerMile
      chargingInternalMiles = internalMiles
      chargingExternalMiles = externalMiles
    }
  }

  let driverWageCost = 0
  const wageRate =
    (vehicleType.labour_hourly_rate != null && vehicleType.labour_hourly_rate > 0
      ? vehicleType.labour_hourly_rate
      : null) ??
    driverWageRatePerHour ??
    config?.driver_wage_rate_per_hour ??
    0
  // Lease and charging are per vehicle, so crew multiplies labour only.
  const crew = crewSize != null && crewSize > 1 ? crewSize : 1
  if (durationMinutes != null && durationMinutes > 0 && wageRate > 0) {
    driverWageCost = (durationMinutes / 60) * wageRate * crew
  }

  const totalCost = vehicleLeaseCost + chargingCost + driverWageCost

  let costPerParcel: number | undefined
  if (totalForecastVolume != null && totalForecastVolume > 0) {
    costPerParcel = totalCost / totalForecastVolume
  }

  return {
    vehicle_lease_cost: vehicleLeaseCost,
    charging_cost: chargingCost,
    charging_internal_miles: chargingInternalMiles,
    charging_external_miles: chargingExternalMiles,
    driver_wage_cost: driverWageCost,
    total_cost: totalCost,
    cost_per_parcel: costPerParcel,
    total_forecast_volume: totalForecastVolume ?? undefined,
    total_haulage_delivery_revenue: totalHaulageDeliveryRevenue ?? 0,
  }
}

export type PricedStop = StopWithHaulageRevenue &
  StopWithDirectInjectionCost & {
    volume_forecast?: number | null
    volumeEstimated?: number | null
  }

export function findVehicleType<T extends { id?: string; name: string; display_name?: string | null }>(
  vehicleTypes: readonly T[] | null | undefined,
  ref: string | null | undefined,
): T | null {
  if (!ref || !vehicleTypes) return null
  return vehicleTypes.find((vt) => vt.id === ref || vt.name === ref || vt.display_name === ref) ?? null
}

/** Forecast parcels across a route's stops; null when there are none, so no cost per parcel is shown. */
export function sumForecastVolume(stops: PricedStop[] | null | undefined): number | null {
  const total = (stops ?? []).reduce(
    (sum, s) => sum + (Number(s.volume_forecast ?? s.volumeEstimated) || 0),
    0,
  )
  return total > 0 ? total : null
}

/** Null when neither side has a haulage delivery ("not applicable"); 0 when one does but is unpriced. */
export function resolveHaulageRevenue(
  dayStops: PricedStop[] | null | undefined,
  planStops: PricedStop[] | null | undefined,
): number | null {
  const isHaulage = (s: PricedStop) =>
    s.slotType === "haulage_delivery" || s.stop_type === "haulage_delivery"
  if (!(dayStops ?? []).some(isHaulage) && !(planStops ?? []).some(isHaulage)) return null
  return sumHaulageDeliveryRevenueFromStops(dayStops) || sumHaulageDeliveryRevenueFromStops(planStops)
}

export interface PricedRouteSide {
  stops?: PricedStop[] | null
  crewSize?: number | null
  routeKind?: string | null
}

export interface PriceRouteInput<V extends RouteCostVehicleType & { id?: string; display_name?: string | null }> {
  vehicleTypes: readonly V[] | null | undefined
  vehicleTypeRef: string | null | undefined
  config: RouteCostConfig | null | undefined
  distanceMiles: number | null | undefined
  durationMinutes: number | null | undefined
  day?: PricedRouteSide | null
  plan?: PricedRouteSide | null
  monthlyLeaseCostOverride?: number | null
}

/** Distance and duration stay with the caller, since day actuals vs plan is what distinguishes screens. */
export function priceRoute<V extends RouteCostVehicleType & { id?: string; display_name?: string | null }>(
  input: PriceRouteInput<V>,
): RouteCostBreakdown | null {
  const dayStops = input.day?.stops
  const planStops = input.plan?.stops
  return calculateRouteCost({
    vehicleType: findVehicleType(input.vehicleTypes, input.vehicleTypeRef),
    distanceMiles: input.distanceMiles,
    durationMinutes: input.durationMinutes,
    config: input.config,
    totalForecastVolume: sumForecastVolume(dayStops?.length ? dayStops : planStops),
    totalHaulageDeliveryRevenue: resolveHaulageRevenue(dayStops, planStops),
    directInjectionCostGbp:
      resolveDirectInjectionCost(dayStops) ?? resolveDirectInjectionCost(planStops),
    crewSize: resolveCrewSize(input.day?.crewSize, input.plan?.crewSize),
    routeKind: resolveRouteKind(input.day?.routeKind, input.plan?.routeKind),
    monthlyLeaseCostOverride: input.monthlyLeaseCostOverride,
  })
}
